import { SecondaryPageLink } from '@/PageManager'
import { generateBech32IdFromATag, tagNameEquals } from '@/lib/tag'
import { cn } from '@/lib/utils'
import { Event } from '@nostr/tools/wasm'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Content from '../Content'
import { GEOCACHE_VERIFICATION } from './GeocacheListing'

export default function GeocacheFoundLog({
  event,
  className
}: {
  event: Event
  className?: string
}) {
  const { t } = useTranslation()
  const aTag = useMemo(() => event.tags.find(tagNameEquals('a')), [event])
  const cacheNaddr = useMemo(() => (aTag ? generateBech32IdFromATag(aTag) : undefined), [aTag])
  const cacheIdentifier = aTag ? aTag[1].split(':')[2] : undefined
  const images = useMemo(
    () =>
      event.tags
        .filter((t) => t[0] === 'image')
        .map((t) => t[1])
        .filter(Boolean),
    [event]
  )
  const verificationRaw = useMemo(
    () => event.tags.find(tagNameEquals('verification'))?.[1],
    [event]
  )
  const verified = useMemo(() => {
    if (!verificationRaw) return null
    try {
      const parsed = JSON.parse(verificationRaw) as Event
      if (parsed.kind !== GEOCACHE_VERIFICATION) return null
      return parsed
    } catch {
      return null
    }
  }, [verificationRaw])

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-2">
        <div className="font-medium">{t('Found log')}</div>
        {verified && (
          <span className="text-xs px-2 py-0.5 rounded bg-green-400/20 text-green-700 dark:text-green-400">
            {t('verified')}
          </span>
        )}
      </div>
      {cacheNaddr && (
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{t('Cache')}: </span>
          <SecondaryPageLink
            to={`/${cacheNaddr}`}
            className="text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {cacheIdentifier || cacheNaddr}
          </SecondaryPageLink>
        </div>
      )}
      <Content event={event} />
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((src) => (
            <img key={src} src={src} alt="" className="max-h-48 rounded-lg object-cover" />
          ))}
        </div>
      )}
    </div>
  )
}
